import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CartService } from './cart.service';
import { ConfigService } from './config.service';

// Formato alineado con SubmitOrderRequest del backend (nats/order_handlers.rs)
export interface ExternalOrderRequest {
  company_hash: string;
  session_id: string;
  service_type: string;
  service_reference?: string;
  customer_name?: string;
  customer_phone?: string;
  customer_address?: string;
  table_number?: string;
  items: ExternalOrderItem[];
  packs?: ExternalOrderPack[];
  customer_note?: string;
  total_amount?: number;
  subtotal?: number;
  currency?: string;
}

export interface ExternalOrderItem {
  product_id: number;
  product_name: string;
  quantity: number;
  unit_price: number;
  variant_id?: number;
  variant_name?: string;
  pack_id?: number;
  pack_name?: string;
  customizations?: {
    without?: string[];
    extras?: { name: string; price?: number }[];
    note?: string;
  };
}

export interface ExternalOrderPack {
  pack_id: number;
  pack_name: string;
  pack_price: number;
  item_product_ids: number[];
}

export interface OrderResponse {
  success: boolean;
  order_id?: string;
  session_id?: string;
  message?: string;
  error?: string;
}

export interface OrderItemDetail {
  product_id: number;
  product_name: string;
  quantity: number;
  unit_price: number;
  customizations?: {
    without?: string[];
    extras?: { name: string; price?: number }[];
    note?: string;
    cooking_point?: string;
    size?: string;
  };
}

export interface OrderHistoryItem {
  id: string;
  business_id: string;
  business_name?: string;
  tpv_order_id?: string;
  session_id?: string;
  service_type: string;
  total_amount: string;
  currency: string;
  items_count: number;
  order_items?: OrderItemDetail[];
  status: string;
  ordered_at: string;
  completed_at?: string;
  order_ref?: string;
  estimated_minutes?: number;
}

export interface OrderHistoryResponse {
  success: boolean;
  orders: OrderHistoryItem[];
}

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  private http = inject(HttpClient);
  private cartService = inject(CartService);
  private config = inject(ConfigService);

  submitOrder(customerName: string, customerPhone?: string, specialRequests?: string, allergens?: string[]): Observable<OrderResponse> {
    const cart = this.cartService.cart();
    if (!cart || cart.items.length === 0) {
      return of({ success: false, message: 'El carrito esta vacio' });
    }

    // Mapear order_mode del carrito al service_type del backend
    const serviceType = this.mapOrderModeToServiceType(cart.order_mode);

    // Build items with correct product_id, variant_id, pack_id
    const items: ExternalOrderItem[] = cart.items.map(item => {
      // Resolve real product_id: use real_product_id if available, fallback to parsing product_id string
      let productId = item.real_product_id ?? parseInt(item.product_id, 10);
      if (isNaN(productId)) productId = 0;

      const orderItem: ExternalOrderItem = {
        product_id: productId,
        product_name: item.name,
        quantity: item.quantity,
        unit_price: item.price,
        variant_id: item.variant_id ?? undefined,
        variant_name: item.variant_name ?? undefined,
        pack_id: item.pack_id ?? undefined,
        pack_name: item.pack_name ?? undefined,
      };

      // Build customizations (exclude 'change' type which is pack metadata for display only)
      const without = item.customizations
        .filter(c => c.type === 'remove')
        .map(c => c.ingredient);

      // Use stored extra_prices for accurate pricing
      const extras = item.customizations
        .filter(c => c.type === 'add')
        .map(c => {
          const storedPrice = item.extra_prices?.find(ep => ep.name === c.ingredient);
          return { name: c.ingredient, price: storedPrice?.price ?? undefined };
        });

      if (without.length > 0 || extras.length > 0 || item.notes) {
        orderItem.customizations = {
          without: without.length > 0 ? without : undefined,
          extras: extras.length > 0 ? extras : undefined,
          note: item.notes || undefined,
        };
      }

      return orderItem;
    });

    // Build packs array from cart items (group by pack_id)
    const packsMap = new Map<number, ExternalOrderPack>();
    for (const item of cart.items) {
      if (item.pack_id && item.pack_name) {
        if (!packsMap.has(item.pack_id)) {
          // Find the first item of this pack (carries the total price)
          const firstPackItem = cart.items.find(i => i.pack_id === item.pack_id && i.price > 0);
          packsMap.set(item.pack_id, {
            pack_id: item.pack_id,
            pack_name: item.pack_name,
            pack_price: firstPackItem?.price ?? 0,
            item_product_ids: [],
          });
        }
        const realId = item.real_product_id ?? parseInt(item.product_id, 10);
        if (realId && !isNaN(realId)) {
          packsMap.get(item.pack_id)!.item_product_ids.push(realId);
        }
      }
    }
    const packs = packsMap.size > 0 ? Array.from(packsMap.values()) : undefined;

    // Append allergens to customer_note
    let customerNote = specialRequests || undefined;
    if (allergens && allergens.length > 0 && customerNote) {
      // Already included in specialRequests from order-confirmation component
    }

    const request: ExternalOrderRequest = {
      company_hash: cart.restaurant_id,
      session_id: crypto.randomUUID(), // Unique per order
      service_type: serviceType,
      table_number: cart.terminal_suffix !== 'default' ? cart.terminal_suffix : undefined,
      customer_name: customerName || undefined,
      customer_phone: customerPhone || undefined,
      items,
      packs,
      customer_note: customerNote,
      total_amount: this.cartService.totalPrice(),
      subtotal: this.cartService.totalPrice(),
      currency: 'EUR',
    };

    const baseUrl = this.config.getApiUrl();

    return this.http.post<OrderResponse>(`${baseUrl}/api/v1/qr/external-order`, request).pipe(
      map(response => {
        if (response.success) {
          this.cartService.clearCart();
        }
        return response;
      }),
      catchError(error => {
        console.error('Error submitting order:', error);
        const message = error.error?.message || error.error?.error || 'Error al enviar el pedido. Intentalo de nuevo.';
        return of({ success: false, message });
      })
    );
  }

  deleteOrder(orderId: string): Observable<{ success: boolean; error?: string }> {
    const baseUrl = this.config.getApiUrl();
    return this.http.delete<{ success: boolean; error?: string }>(
      `${baseUrl}/api/v1/orders/${orderId}`
    ).pipe(
      catchError(error => {
        return of({ success: false, error: error.error?.error || 'Error al eliminar' });
      })
    );
  }

  getOrderHistory(limit = 20, offset = 0): Observable<OrderHistoryResponse> {
    const baseUrl = this.config.getApiUrl();
    return this.http.get<OrderHistoryResponse>(
      `${baseUrl}/api/v1/orders/history`,
      { params: { limit: limit.toString(), offset: offset.toString() } }
    ).pipe(
      catchError(error => {
        console.error('Error fetching order history:', error);
        return of({ success: false, orders: [] });
      })
    );
  }

  private mapOrderModeToServiceType(orderMode: string): string {
    switch (orderMode) {
      case 'dine-in': return 'dine-in';
      case 'takeaway': return 'pickup';
      case 'delivery': return 'delivery';
      default: return 'external';
    }
  }
}
